import { Field, FormikErrors, useFormikContext } from 'formik';
import React, { useCallback } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Vessel } from '@sdir/httpclient/lib/clients/core/search';
import styled from 'styled-components';
// eslint-disable-next-line max-len
import { AdditionalCompetenceExtended } from '@sdir/httpclient/lib/clients/aps/additionalcompetence';
import { FormInput, FormSearch, FormSelect } from '@sdir/blueprint.aps/lib/components';
import { SearchResult } from '@sdir/blueprint.aps/lib/components/Molecules/FormSearch';
import { FormLinkSwitch } from '../../../../Molecules';
import { vesselApi } from '../../../../../httpclient';
import { NewVesselInput, SelectOption, VesselSubformInput } from '../../../../../types';

const DEFAULT_REQUIRED = ['name', 'callSign', 'country'];

export const validateVesselInput = (
  values: VesselSubformInput,
  intl: IntlShape,
  required?: string[]
) => {
  const requiredText = intl.formatMessage({ id: 'general.form.error' });
  const requiredFields = required || DEFAULT_REQUIRED;

  const errors: FormikErrors<VesselSubformInput> = {};

  if (values.useExisting) {
    if (!values.existingVessel) errors.existingVessel = requiredText;
  } else {
    const newVesselErrors: FormikErrors<NewVesselInput> = {};
    requiredFields?.forEach((fieldName) => {
      if (!values.newVessel?.[fieldName]) newVesselErrors[fieldName] = requiredText;
    });
    if (Object.keys(newVesselErrors).length) errors.newVessel = newVesselErrors;
  }

  return Object.keys(errors).length ? errors : undefined;
};

export const createInitialVesselInput = (
  existingAdditionalCompetence?: AdditionalCompetenceExtended
): VesselSubformInput => {
  const vessel = existingAdditionalCompetence?.vessel;
  return {
    useExisting: true,
    existingVessel: vessel
      ? { name: vessel.name, callSign: vessel.callsign, imoNumber: vessel.imoNumber, id: vessel.id }
      : null,
    newVessel: {
      name: '',
      imoNumber: '',
      callSign: '',
      country: '',
      registry: '',
    },
  };
};

interface VesselFormProps {
  name: string;
  countryOptions: SelectOption[];
  requiredFields?: string[];
}

export const VesselSubForm: React.FC<VesselFormProps> = ({
  name,
  countryOptions,
  requiredFields = DEFAULT_REQUIRED,
}) => {
  const intl = useIntl();

  const handleVesselSearch = useCallback(
    (query: string): Promise<SearchResult<Vessel>[] | undefined> => {
      return vesselApi.v1VesselSearchfrombodyQueryPost({ query }).then((response) => {
        return (
          response.data.source?.map((v) => ({
            name: v.name || '',
            secondary: v.callSign || undefined,
            value: v,
          })) || []
        );
      });
    },
    []
  );

  const makeFullFieldName = useCallback((fieldName) => `${name}.${fieldName}`, [name]);

  const { getFieldProps } = useFormikContext();

  const fieldProps = getFieldProps<VesselSubformInput>(name);

  return (
    <div>
      {fieldProps.value?.useExisting ? (
        <Field
          name={makeFullFieldName('existingVessel')}
          initialSearchValue={fieldProps.value?.existingVessel?.name}
          placeholder={intl.formatMessage({ id: 'addseagoing.vessel.vesselsearch' })}
          searchOnChange
          component={FormSearch}
          searchCallback={handleVesselSearch}
        />
      ) : (
        <FieldsContainer>
          <Field
            name={makeFullFieldName('newVessel.name')}
            component={FormInput}
            type="text"
            label={`${intl.formatMessage({
              id: 'addseagoing.vessel.newVessel.name',
            })} ${requiredFields?.includes('name') ? '*' : ''}`}
          />
          <Field
            name={makeFullFieldName('newVessel.imoNumber')}
            component={FormInput}
            type="text"
            label={`${intl.formatMessage({
              id: 'addseagoing.vessel.newVessel.imoNumber',
            })} ${requiredFields?.includes('imoNumber') ? '*' : ''}`}
          />
          <Field
            name={makeFullFieldName('newVessel.callSign')}
            component={FormInput}
            type="text"
            label={`${intl.formatMessage({
              id: 'addseagoing.vessel.newVessel.callSign',
            })} ${requiredFields?.includes('callSign') ? '*' : ''}`}
          />
          <Field
            name={makeFullFieldName('newVessel.country')}
            component={FormSelect}
            options={countryOptions}
            searchable
            defaultVal={`${intl.formatMessage({
              id: 'general.inputs.country.required',
            })} ${requiredFields?.includes('country') ? '*' : ''}`}
          />
          <Field
            name={makeFullFieldName('newVessel.registry')}
            component={FormInput}
            type="text"
            label={`${intl.formatMessage({
              id: 'addseagoing.vessel.newVessel.registry',
            })} ${requiredFields?.includes('registry') ? '*' : ''}`}
          />
        </FieldsContainer>
      )}
      <ControlsContainer>
        <Field
          name={makeFullFieldName('useExisting')}
          activeLabel={intl.formatMessage({
            id: 'addseagoing.vessel.addmanualvessel',
          })}
          inactiveLabel={intl.formatMessage({
            id: 'addseagoing.vessel.searchvessel',
          })}
          component={FormLinkSwitch}
        />
      </ControlsContainer>
    </div>
  );
};

const FieldsContainer = styled.div`
  > * {
    margin-bottom: 3rem;
  }
`;

const ControlsContainer = styled.div`
  margin: 0 0 3rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
